import { Icon } from '@iconify/react/dist/iconify.js';
import {
    Box,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Text } from 'recharts';
import CarbonFootprintInfo from '../infomodals/CarbonFootprintInfo';

const COLORS = ['#009D4E', '#D7E02B','#F8931F', '#EE2329', '#b9070d'];
const RADIAN = Math.PI / 180;

const needle = (needle_val, data, cx, cy, iR, oR, color) => {
    let total = 0;
    data.forEach((v) => {
      total += v.value;
    });
    const ang = 180.0 * (1 - needle_val / total);
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * ang);
    const cos = Math.cos(-RADIAN * ang);
    const r = 5;
    const x0 = cx;
    const y0 = cy;
    const xba = x0 + r * sin;
    const yba = y0 - r * cos;
    const xbb = x0 - r * sin;
    const ybb = y0 + r * cos;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;
  
    return [
        <circle key={'needle_circle'} cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
        <path key={'needle_line'} d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`} stroke="#none" fill={color} />,
    ];
};

const renderCustomizedLabel = ( props, handleClick ) => {
    const { id, cx, cy, innerRadius, outerRadius, endAngle, label } = props;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.1;
    const x = cx + radius * Math.cos(-endAngle * RADIAN);
    const y = cy + radius * Math.sin(-endAngle * RADIAN);
    
    if (label) return (
      <Text onClick={() => label ? handleClick(id) : {}} x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={"0.8em"} width={50}>
        {label}
      </Text>
    );
};

export default function ResultGaugeChart({ resultValue  }) {
    const [needleValue, setNeedleValue] = useState(0);

    // For more info popup
    const [popupID, setPopupID] = useState();
    const [openPopup, setOpenPopup] = useState(false);
    const handleOpen = (i=null) => {
        setPopupID(i);
        setOpenPopup(true);
    }
    const handleClose = () => setOpenPopup(false);

    const width = 300;
    const height = 200;
    const cx = 0.5*width;
    const cy = 0.89*height;
    const iR = 50;
    const oR = 100;
    
    const sectorThresholds = [3.02, 6.2, 12.034, 18, 19];
    const pieData = [
        {
            id: 0,
            label: '1.5°C Target',
            value: sectorThresholds[0],
            fulllabel: '1.5°C Target',
            valuelabel: '2.3 t CO2 (~ 3.02 t CO2e)',
            source: 'IEEP, SEI, Oxfam, 2021',
            sourcelink: 'https://oxfamilibrary.openrepository.com/bitstream/handle/10546/621305/bn-carbon-inequality-2030-051121-en.pdf;jsessionid=A75D512853EA3E0C1F09FAB0379E6D4B?sequence=1',
            addtinfo: 'Paris Agreement aligned CO2e per capita target by 2030 : Carbon budget per individual, so that globally we keen within a projected global warming of no more than 1.5 °C ',

        },
        {
            id: 1,
            label: 'Global Avg',
            value: sectorThresholds[1]-sectorThresholds[0],
            fulllabel: 'Global Average',
            valuelabel: '6.2 t CO2e per capita',
            source: 'World Bank, 2019',
            sourcelink: 'https://datatopics.worldbank.org/sdgatlas/goal-13-climate-action/?lang=en',
            addtinfo: 'Global average emissions per individual, as of 2019'
        },
        {
            id: 2,
            label: 'SG Avg',
            value: sectorThresholds[2]-sectorThresholds[1],
            fulllabel: 'Singapore Average',
            valuelabel: '12.034 t CO2e per capita',
            source: 'SGFIN Whitepaper, 2024',
            sourcelink: 'https://sgfin.nus.edu.sg/publications/whitepapers/',
            addtinfo: 'Singapore average emissions per individual, as of 2023'
        },
        {
            id: 3,
            label: '',
            value: sectorThresholds[3]-sectorThresholds[2],
        },
        // {
        //     id: 4, // Extra
        //     label: '',
        //     value: sectorThresholds[4]-sectorThresholds[3]
        // },
    ]

    // SG Average - 12.03 tCO2e
    // ~2.3tCO2 per capita by 2030 (IEEP, SEI, Oxfam, 1.5°C global warming pathway) ~3.02 t CO2e
    // ~4.7tCO2 per capita current global average (IEA, 2023) ~ 6.18 t CO2e

    useEffect(() => {
        if (resultValue > sectorThresholds[3]) {
            // Adjust too large values to fall within highest possible threshold
            let adjVal = ( resultValue/(resultValue+10) * (sectorThresholds[3]-sectorThresholds[2]) ) + sectorThresholds[2];
            setNeedleValue(adjVal);
        } else setNeedleValue(resultValue);
    }, [resultValue])

    return (
        <>
        {/* Minimized Card */}
        <Box width={width} height={height}>
            <ResponsiveContainer>
                <PieChart
                    {...{
                        overflow: 'visible'
                    }}
                >
                    <Pie 
                        data={pieData} dataKey="value" cx="50%" cy="90%" 
                        startAngle={180} endAngle={0}
                        innerRadius="30%"
                        outerRadius="120%"
                        labelLine={false}
                        label={props => renderCustomizedLabel(props, handleOpen)}
                    >
                        {pieData.map((entry, index) => {
                            return <Cell key={entry.id} fill={COLORS[index % COLORS.length]} stroke={''} />
                        })}
                    </Pie>
                    {needleValue && needle(needleValue, pieData, cx, cy, iR, oR, '#000')}
                </PieChart>
            </ResponsiveContainer>
        </Box>
        
        <Box className="centered-y" >
        <Box className="centered-x" sx={{ my: 1, cursor: 'pointer', '&:hover': { color: 'var(--pri-yellow)' } }} >
            <Icon icon="material-symbols:info-outline" fontSize={22} /> 
            <Typography variant="body2" sx={{ fontSize: 12, lineHeight: 1.8 }} onClick={()=>handleOpen()}>&ensp;Learn More</Typography>
        </Box> 
        </Box> 

        {/* Fullscreen Card */}
        <CarbonFootprintInfo open={openPopup} handleClose={handleClose} content={pieData[popupID] || popupID} />
        </>
    );
}
